import React, { useCallback, useEffect, useRef, useState } from 'react';
import TextField, { TextFieldVariants } from '@mui/material/TextField';

interface Props {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  multiline?: boolean;
  sxOptions?: any;
  isError?: boolean;
  variant?: string;
  rows?: number;
  type?: string;
  minRows?: number;
  helperText?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  autofocus?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validator?: (text: string) => boolean;
}

export default function RWTextFieldComponent({
  label = '',
  autofocus = false,
  placeholder,
  disabled = false,
  helperText,
  variant = 'outlined',
  minRows,
  type = 'text',
  rows,
  sxOptions,
  multiline = false,
  id,
  name,
  value,
  isError = false,
  onChange,
  validator,
}: Props) {
  const [inputValue, setInputValue] = useState(value);
  const valueRef = useRef<string | undefined>(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const localChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (validator) {
        validator(v) && setInputValue(v);
        return;
      }
      setInputValue(v);
    },
    [validator]
  );

  const triggerChange = useCallback(() => {
    if (valueRef.current !== inputValue) {
      valueRef.current = inputValue;
      onChange({
        target: { value: inputValue, name: name },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [inputValue, name, onChange]);

  return (
    <>
      <TextField
        id={id}
        autoFocus={autofocus}
        disabled={disabled}
        placeholder={placeholder}
        helperText={helperText}
        type={type}
        minRows={minRows}
        variant={variant as TextFieldVariants}
        sx={sxOptions}
        name={name}
        label={label}
        value={inputValue}
        onChange={localChangeHandler}
        onMouseOut={disabled ? undefined : triggerChange}
        onBlur={disabled ? undefined : triggerChange}
        fullWidth
        rows={rows}
        multiline={multiline}
        error={isError}
      />
    </>
  );
}
